import { Component, Inject, OnInit, OnDestroy, HostListener } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpService } from 'src/app/services/http-service';
import { BeadCanvas } from 'src/app/peditor/beadcanvas';

export interface InsertRowsColsModalData {
  operation: 'insert' | 'delete';
  mode: 'rows' | 'cols';
  currentCount: number;
  bcanvas?: BeadCanvas;
}

export interface InsertRowsColsModalResult {
  position: number;
  count: number;
}

@Component({
  selector: 'insert-rows-cols-modal',
  templateUrl: './insert-rows-cols-modal.html',
  styleUrls: ['./insert-rows-cols-modal.css'],
})
export class InsertRowsColsModal implements OnInit, OnDestroy {
  position: number;
  count: number = 1;
  insertBefore: boolean = true;
  formSubmitted = false;

  constructor(
    public httpService: HttpService,
    public dialogRef: MatDialogRef<InsertRowsColsModal>,
    @Inject(MAT_DIALOG_DATA) public data: InsertRowsColsModalData
  ) {}

  ngOnInit() {
    this.position = Math.floor(this.data.currentCount / 2) + 1;
    this.updateHighlight();
  }

  onCanvasClick = (clickedPosition: number) => {
    // Convert from zero-based to one-based position
    this.position = clickedPosition + 1;
    this.updateHighlight();
  }

  ngOnDestroy() {
    if (this.data.bcanvas) {
      this.data.bcanvas.clearHighlight();
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.movePrevious();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.moveNext();
    }
  }

  movePrevious() {
    if (this.position > 1) {
      this.position--;
      this.updateHighlight();
    }
  }

  moveNext() {
    const maxPosition = this.data.operation === 'insert' ? this.data.currentCount + 1 : this.data.currentCount;
    if (this.position < maxPosition) {
      this.position++;
      this.updateHighlight();
    }
  }

  onPositionChange() {
    this.updateHighlight();
  }

  onInsertBeforeChange() {
    this.updateHighlight();
  }

  onCountChange() {
    this.updateHighlight();
  }

  private updateHighlight() {
    if (!this.data.bcanvas) {
      return;
    }

    let zeroBasedPosition = this.position - 1;

    if (this.data.operation === 'insert') {
      if (!this.insertBefore) {
        zeroBasedPosition += 1;
      }
      if (this.data.mode === 'rows') {
        this.data.bcanvas.setHighlightRow(zeroBasedPosition, this.count, this.onCanvasClick);
      } else {
        this.data.bcanvas.setHighlightCol(zeroBasedPosition, this.count, this.onCanvasClick);
      }
    } else {
      // For delete, highlight the rows/cols that will be deleted
      if (this.data.mode === 'rows') {
        this.data.bcanvas.setHighlightRow(zeroBasedPosition, this.count, this.onCanvasClick);
      } else {
        this.data.bcanvas.setHighlightCol(zeroBasedPosition, this.count, this.onCanvasClick);
      }
    }
  }

  getTitle(): string {
    if (this.data.operation === 'insert') {
      return this.data.mode === 'rows' ? 'Insert Rows' : 'Insert Columns';
    } else {
      return this.data.mode === 'rows' ? 'Remove Rows' : 'Remove Columns';
    }
  }

  getPositionLabel(): string {
    return this.data.mode === 'rows' ? 'Row Number' : 'Column Number';
  }

  getCountLabel(): string {
    return this.data.mode === 'rows' ? 'Number of Rows' : 'Number of Columns';
  }

  getInsertBeforeLabel(): string {
    return this.data.mode === 'rows' ? 'Insert above this row' : 'Insert left of this column';
  }

  getInsertAfterLabel(): string {
    return this.data.mode === 'rows' ? 'Insert below this row' : 'Insert right of this column';
  }

  getHintText(): string {
    const itemType = this.data.mode === 'rows' ? 'row' : 'column';

    if (this.data.operation === 'insert') {
      const direction = this.insertBefore
        ? this.data.mode === 'rows' ? 'above' : 'to the left of'
        : this.data.mode === 'rows' ? 'below' : 'to the right of';

      return `${this.count} blank ${itemType}${this.count > 1 ? 's' : ''} will be inserted ${direction} ${itemType} ${this.position}`;
    } else {
      const endPosition = this.position + this.count - 1;
      if (this.count === 1) {
        return `${itemType.charAt(0).toUpperCase() + itemType.slice(1)} ${this.position} will be removed`;
      } else {
        return `${this.count} ${itemType}s (${this.position} to ${endPosition}) will be removed`;
      }
    }
  }

  isValid(): boolean {
    if (this.data.operation === 'insert') {
      return (
        this.position >= 1 &&
        this.position <= this.data.currentCount + 1 &&
        this.count >= 1 &&
        this.count <= 50
      );
    } else {
      // For delete, position must be valid and we can't delete more than available
      return (
        this.position >= 1 &&
        this.position <= this.data.currentCount &&
        this.count >= 1 &&
        this.position + this.count - 1 <= this.data.currentCount
      );
    }
  }

  onSubmit() {
    this.formSubmitted = true;
    if (this.isValid()) {
      let zeroBasedPosition = this.position - 1;

      if (this.data.operation === 'insert' && !this.insertBefore) {
        zeroBasedPosition += 1;
      }

      const result: InsertRowsColsModalResult = {
        position: zeroBasedPosition,
        count: this.count,
      };

      this.dialogRef.close(result);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Validates number input and enforces min/max constraints for position field
   * Used to avoid Material Input's setSelectionRange error on number type inputs (iOS Safari)
   */
  validatePositionInput(event: any): void {
    const input = event.target;

    // Remove non-numeric characters
    input.value = input.value.replace(/[^0-9]/g, '');

    // Parse the cleaned value
    const value = parseInt(input.value, 10);

    const max = this.data.operation === 'insert' ? this.data.currentCount + 1 : this.data.currentCount;

    // Only enforce max during typing, allow partial values below min
    if (!isNaN(value)) {
      if (value > max) {
        this.position = max;
        input.value = max.toString();
      } else {
        this.position = value;
      }
    } else {
      this.position = null;
    }

    this.updateHighlight();
  }

  /**
   * Enforces minimum value constraint for position when input loses focus
   */
  validatePositionOnBlur(): void {
    if (this.position === null || this.position < 1) {
      this.position = 1;
    }
    this.updateHighlight();
  }

  /**
   * Validates number input and enforces min/max constraints for count field
   * Used to avoid Material Input's setSelectionRange error on number type inputs (iOS Safari)
   */
  validateCountInput(event: any): void {
    const input = event.target;

    // Remove non-numeric characters
    input.value = input.value.replace(/[^0-9]/g, '');

    // Parse the cleaned value
    const value = parseInt(input.value, 10);

    const max = this.data.operation === 'insert' ? 50 : this.data.currentCount;

    // Only enforce max during typing, allow partial values below min
    if (!isNaN(value)) {
      if (value > max) {
        this.count = max;
        input.value = max.toString();
      } else {
        this.count = value;
      }
    } else {
      this.count = null;
    }

    this.updateHighlight();
  }

  /**
   * Enforces minimum value constraint for count when input loses focus
   */
  validateCountOnBlur(): void {
    if (this.count === null || this.count < 1) {
      this.count = 1;
    }
    this.updateHighlight();
  }
}
